// 遊戲 loading（Loading #2）版面專屬美術載入 — 沿用 Unity Slot026 LoadingUI.prefab 逐節點引用的資源。
// 這批圖在「其餘大批遊戲資源」之前優先載入，讓遊戲 loading 版面能即時顯示（combo 疊層淡出後無縫接棒）。
//
// 對照 prefab（見 docs 掃描）各節點資源：
//   bg=bg_loading（背景）、character=bg_loadingCharacter（角色靜態圖，非 Spine）、
//   barBg=pic_barBg（進度條底，左右鏡像兩片）、barFill=pic_bar（SlicedFilledImage 填充，左→右）、
//   blur=pic_blur（右側輪播暈光底）、page1~3=pic_load_2/pic_load_3/pic_load_all（輪播三頁示意圖）、
//   bottomBar=pic_loadDark（底部深色條）、continueBtn=btn_win（Continue 鈕）、brandLogo=logo_combo（平台品牌）。
//   gameLogo=Art/Game/<lang>/logo.png（頂部遊戲標題，依語系；13 語系皆有，見 gameLogoUrl）。

import { type Texture } from 'pixi.js'
import { ensureAtlases, loadTexture } from '../../framework/assets/spriteAtlas'
import { getLocale } from '../../framework/i18n/i18n'
import { loadNumberFonts } from './NumberFont'

const URLS = {
  bg: '/images/loading/bg_loading.webp',
  character: '/images/loading/bg_loadingCharacter.webp',
  barBg: '/images/loading/pic_barBg.webp',
  barFill: '/images/loading/pic_bar.webp',
  blur: '/images/loading/pic_blur.webp',
  page1: '/images/loading/pic_load_2.webp',
  page2: '/images/loading/pic_load_3.webp',
  page3: '/images/loading/pic_load_all.webp',
  bottomBar: '/images/loading/pic_loadDark.webp',
  continueBtn: '/images/loading/btn_win.webp',
  brandLogo: '/images/loading/logo_combo.webp',
  arrow: '/images/loading/arrow.webp',
} as const

export type LoadingKey = keyof typeof URLS | 'gameLogo'

/**
 * 頂部遊戲標題 logo 依語系選圖（Unity 為各語系一張，見 Art/Game/<lang>/logo.png）。
 * 繁中/簡中兩張放在 `images/ui/`，被歸進 **loading 合圖**（見 scripts/pack-atlases.mjs 的分組說明）——
 * loading 版面比 UI 早一大段時間就要顯示、不能等 ui 合圖，故這兩個語系走合圖、0 額外請求。
 * 其餘 11 語系的 logo 只在 `i18n-<locale>` 合圖內（loading 階段尚未載入），
 * 退回單張載入（loadTexture 找不到快取就 Assets.load 該小圖，每語系一筆）。
 */
function gameLogoUrl(): string {
  const locale = getLocale()
  if (locale === 'zh-Hant') return '/images/ui/logo.webp'
  if (locale === 'zh-Hans') return '/images/ui/logo_hans.webp'
  return `/images/i18n/${locale}/logo.webp`
}

let cache: ReadonlyMap<LoadingKey, Texture> | null = null

/** 優先載入遊戲 loading 版面所需的全部貼圖。第一次顯示 LoadingView 前呼叫（見 main.ts）。 */
export async function loadLoadingAssets(): Promise<void> {
  if (cache !== null) return

  // 先取 loading 合圖（14 張收斂成 1 筆請求，開機第一筆圖片請求就只有這一筆）。
  await ensureAtlases('loading')

  const entries: Array<readonly [LoadingKey, string]> = [
    ...(Object.entries(URLS) as Array<[LoadingKey, string]>),
    ['gameLogo', gameLogoUrl()],
  ]
  const [loaded] = await Promise.all([
    Promise.all(entries.map(async ([key, url]) => [key, await loadTexture(url)] as const)),
    // 輪播第三頁「30,000X」用的位圖字型（tx_loading，底圖同在 loading 合圖內＝不會多一筆請求）。
    loadNumberFonts('loading'),
  ])
  cache = new Map(loaded)
}

export function getLoadingTexture(key: LoadingKey): Texture {
  if (cache === null) {
    throw new Error('遊戲 loading 美術尚未載入，請先呼叫 loadLoadingAssets()')
  }
  const texture = cache.get(key)
  if (texture === undefined) {
    throw new Error(`找不到 loading 美術：${key}`)
  }
  return texture
}
